import { Badge, BadgeVariantType, CollapsedContent } from '@aqarmap/aqarmap-ui';
import { useTranslation } from 'react-i18next';
import { SubscriptionStatus } from '~myAccount/enums';
import { getSubscriptionServices } from '~/utils';
import { formatDate, formatNumber } from '~/lib';
import { UserSubscribeType } from '~/types/subscriptionTypes';
import { LocalesType } from '~/types/uiTypes';
import SubscriptionCta from '~myAccount/pages/UserSubscription/SubscriptionCta';
import RenewalDateSection from '~myAccount/pages/UserSubscription/RenewalDateSection';
import GracePeriodSection from '~myAccount/pages/UserSubscription/GracePeriodSection';
import useSubscriptionStatus from '~myAccount/pages/UserSubscription/hooks/useSubscriptionStatus';

export default function SubscriptionDetails({
  id,
  subscription_plan,
  status,
  next_renewal_date,
}: UserSubscribeType) {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const services = getSubscriptionServices(subscription_plan);
  const { getStatusData } = useSubscriptionStatus();

  const showRenewalDateSection =
    status === SubscriptionStatus.ACTIVE && next_renewal_date;
  const showGracePeriodSection =
    status === SubscriptionStatus.GRACE_PERIOD && next_renewal_date;

  const statusData = getStatusData(status);

  const renewalDate = formatDate({
    value: next_renewal_date,
    language: locale as LocalesType,
    format: 'gregorian',
  });

  const duration = Number(subscription_plan?.duration_days) / 30;
  const fees = `${formatNumber(Number(subscription_plan?.price))} ${t('egp')}/${duration === 1 ? ` ${t('month')}` : ` ${duration} ${t('months')}`}`;

  return (
    <div className="flex flex-col gap-2x">
      <div className="flex flex-col gap-2x px-3x">
        <div className=" flex flex-col gap-1.5x ">
          <div className="flex justify-between items-center ">
            <div className="flex gap-x items-center ">
              <p className="text-title_4 text-black">
                {subscription_plan.title}
              </p>
              <Badge
                text={statusData.text}
                variant={statusData.variant as BadgeVariantType}
                className="text-body_2 min-w-[48px] rounded-[6px]"
              />
            </div>
            <SubscriptionCta
              status={status}
              planId={subscription_plan.id}
              id={id}
            />
          </div>
          <p className="text-link text-gray__dark_2 ">{fees}</p>
        </div>

        <div className="pt-x  border-t border-gray">
          <CollapsedContent
            maxLines={3}
            itemHeight={24}
            itemsGap={8}
            text={{ more: t('readMore'), less: t('readLess') }}
          >
            {services.length > 0 ? (
              <ul className="flex flex-col gap-x">
                {services.map((planService) => {
                  return (
                    <li
                      className={`before:content-['•']  text-body_1 text-gray__dark_2 ${locale === 'en' ? 'before:mr-2' : 'before:ml-2'}`}
                    >
                      {planService.service.title}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <></>
            )}
          </CollapsedContent>
        </div>
      </div>
      {showRenewalDateSection && (
        <RenewalDateSection id={id} renewalDate={renewalDate} />
      )}
      {showGracePeriodSection && (
        <GracePeriodSection renewalDate={renewalDate} />
      )}
    </div>
  );
}
